import { pick, omit } from 'lodash';
import React from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';

import { formatObjectToISO, deleteEmptyValues } from '@src/helpers';
import { useStore } from '@src/models';
import { FormikWizard } from '@src/features/common';
import { handleFormikSequentialSubmit } from 'src/helpers';
import { ROUTES } from 'src/constants';
import { useSearchParams } from 'src/hooks';
import {
  getGeneralFormSchema,
  getCvFormSchema,
  getExperiencesFormSchema,
  getEducationsFormSchema,
  getValidationSchema,
  ValuesType
} from './validationSchema';
import GeneralForm from './GeneralForm';
import ExperiencesForm from './ExperiencesForm';
import EducationsForm from './EducationsForm';
import CVForm from './CVForm';

type ApplicationWizardFormProps = {
  stepNumber: number;
  setStepNumber: (stepNumber: number) => void;
};

const submitFileAnswers = async ({ custom_answers, jobsStore, jobId }) => {
  await Promise.all(
    custom_answers.map(async (answer, index) => {
      if (answer instanceof FormData) {
        const file_url = await jobsStore.uploadCustomFileAnswer({ jobId, file: answer });
        custom_answers[index] = file_url;
      } else {
        custom_answers[index] = answer;
      }
    })
  );
};

export const ApplicationWizardForm = observer(({ stepNumber, setStepNumber }: ApplicationWizardFormProps) => {
  const { jobId } = useParams();
  const { routerStore, resourcesStore, authStore, userStore, jobsStore } = useStore();
  const job = jobsStore.getJobById(jobId);
  const { inviter_id } = useSearchParams();

  React.useEffect(() => {
    setStepNumber(0);
  }, [setStepNumber, authStore.isAuthorized]);
  const onSubmit = React.useCallback(
    async (values, actions) => {
      const { photo_file, cv_file, languages_attributes, experiences_attributes, degrees_attributes, ...rest } = values;

      // if one of custom answers is file then
      // we admit that it is answer to file upload custom question
      await submitFileAnswers({ custom_answers: rest.custom_answers, jobsStore, jobId });

      const payload = job.skipExternalUrl
        ? {
          ...rest,
          languages_attributes: (languages_attributes || []).map(({ id, ...language }) => ({
            ...language,
            original_entity_id: id
          })),
          experiences_attributes: (experiences_attributes || []).map(({ id, ...experience }) => {
            if (experience._destroy) return experience;

            const is_current_job = experience.endDate.month === 'Current';
            return {
              ...experience,
              original_entity_id: id,
              start: formatObjectToISO(experience?.startDate || {}),
              finish: is_current_job ? null : formatObjectToISO(experience?.endDate || {}),
              is_current_job,
              main_theme_id: experience.main_theme.theme_id,
              sub_theme_id: experience.sub_theme.theme_id,
              skills_attributes: experience.skills_attributes.map(item => omit(item, 'id'))
            };
          }),
          degrees_attributes: (degrees_attributes || []).map(({ id, ...degree }) => {
            if (degree._destroy) return degree;

            return {
              ...degree,
              original_entity_id: id,
              start: formatObjectToISO(degree?.startDate || {}),
              grade: formatObjectToISO(degree?.endDate || {})
            };
          }),
          custom_answers: { ...(values.custom_answers || []) }
        }
        : {
          ...values,
          languages_attributes: [],
          experiences_attributes: [],
          degrees_attributes: [],
          custom_answers: {}
        };
      const requests = [];

      if (cv_file) {
        requests.push(() =>
          jobsStore.uploadCV({ jobId, payload: cv_file }).then(({ resume_url }) => {
            if (resume_url && /^https?:\/\//.test(resume_url)) {
              payload.remote_resume_url = resume_url;
            }
          })
        );
      } else {
        // Only assign if it's present and a valid http(s) URL
        const { remote_resume_url } = payload;
        if (
          !remote_resume_url ||
          typeof remote_resume_url !== 'string' ||
          !/^https?:\/\//.test(remote_resume_url)
        ) {
          delete payload.remote_resume_url;
        }
      }
      requests.push(() => jobsStore.applyForJob(job, payload, { params: { inviter_id: inviter_id } }));

      await handleFormikSequentialSubmit({
        requests,
        actions,
        successMsg: 'You have successfully applied!',
        shouldPropagateError: true
      });
      actions.resetForm({ values });
      if (routerStore.searchParams.standalone) {
        window.location.href = ROUTES.employer.careers;
      } else {
        // routerStore.push(ROUTES.candidatesRoutes.preferences.base);
        window.location.href = ROUTES.home
      }
    },
    [job, jobId, jobsStore, routerStore]
  );

  const initialValues = React.useMemo(() => {
    const validationSchema = getValidationSchema(job.application_form_settings);
    const defaultValues = validationSchema.default();
    return {
      ...defaultValues,
      ...pick(deleteEmptyValues(userStore.initialUserInfo), Object.keys(defaultValues)),
      custom_answers: [...Array(job.application_form_settings?.formatted?.custom_questions?.length)],
      preferNotToSayGender: userStore.initialUserInfo?.gender === 'not_prefer',
      remote_resume_url: userStore.initialUserInfo?.resume_url
    };
  }, [job.application_form_settings, userStore.initialUserInfo]);

  return (
    <>
      <FormikWizard<ValuesType>
        {...{ initialValues, onSubmit, stepNumber, withAutoFocus: false }}
        onStepChange={setStepNumber}
      >
        <GeneralForm
          validationSchema={getGeneralFormSchema(job.application_form_settings)}
          {...{ authStore, userStore, resourcesStore, job }}
        />
        {job.application_form_settings?.professional_experience && job.skipExternalUrl && (
          <ExperiencesForm
            validationSchema={getExperiencesFormSchema(job.application_form_settings)}
            resourcesStore={resourcesStore}
            isAnonymousJob={job.is_anonymous}
          />
        )}
        {job.application_form_settings?.education && job.skipExternalUrl && (
          <EducationsForm
            validationSchema={getEducationsFormSchema(job.application_form_settings)}
            resourcesStore={resourcesStore}
            isAnonymousJob={job.is_anonymous}
          />
        )}
        {job.skipExternalUrl && (
          <CVForm
            validationSchema={getCvFormSchema(job.application_form_settings)}
            resourcesStore={resourcesStore}
            user={userStore.user}
            refetchCv={userStore.refetchCv}
            job={job}
            isFetching={Boolean(jobsStore.isFetching)}
          />
        )}
      </FormikWizard>
    </>
  );
});
